import React from "react";
import styled from "styled-components";
import { FONTS, COLORS, BREAKPOINTS } from "../../utils/styleConstants";
import { downloadOfferFile, downloadReformulationFile } from "../../services/api";
import { formatDateToCustomFormat } from '../../utils/dateUtils';
import { formatAmountForDisplay } from '../../utils/amountUtils';

// Using the same colors as PreviewModal for consistency
const DARK_BLUE = "#20376e";
const ACCENT_ORANGE = "#ff7057";

// Updated Container Styles
const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 2rem;
  flex: 1;
`;

// Title styling from OfertaStep1.js
const Title = styled.h1`
  color: ${COLORS.PRIMARY};
  font-size: 2.5rem;
  font-weight: 600;
  font-family: ${FONTS.PRIMARY};
  margin-bottom: 0.5rem;
`;

// Description styling from OfertaStep1.js
const Description = styled.p`
  color: ${COLORS.ACCENT};
  font-size: 1.1rem;
  font-family: ${FONTS.PRIMARY};
  margin-bottom: 2rem;
  line-height: 1.5;
  max-width: 80%;

  @media (max-width: ${BREAKPOINTS.MOBILE}) {
    max-width: 100%;
  }
`;

const InfoHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 0.5rem;
  /* Removed border-bottom */
  
  @media (max-width: ${BREAKPOINTS.MOBILE}) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
`;

// Updated to black color as requested
const HeaderDate = styled.div`
  font-size: 0.9rem;
  font-weight: 400;
  color: #333;
  border-bottom: 2px solid #333;
  display: inline-block;
  padding-bottom: 0.25rem;
  
  & > span {
    margin-right: 0.5rem;
    font-weight: 600;
  }
`;

// Content Styles - updated to match PreviewModal
const InfoBlock = styled.div`
  margin-bottom: 1rem;
  border-bottom: 1px solid #d5d5d5;
  padding-bottom: 1rem;
  
  &:last-child {
    border-bottom: none;
  }
`;

// Updated to match PreviewModal styling
const BlockTitle = styled.h3`
  font-size: 1rem;
  font-weight: 400;
  color: ${COLORS.SECONDARY};
  margin-bottom: 0.75rem;
  font-family: ${FONTS.PRIMARY};
  letter-spacing: 0.01em;
`;

const TableGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.5rem;
  
  @media (max-width: ${BREAKPOINTS.MOBILE}) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const InfoItem = styled.div`
  font-size: 0.9rem;
  font-family: ${FONTS.SECONDARY};
  color: ${COLORS.SECONDARY};
  font-weight: 400;
  padding: 0.25rem;
`;

const NameItem = styled(InfoItem)`
  grid-column: 1;
  font-weight: 500;
`;

const IdItem = styled(InfoItem)`
  grid-column: 2;
`;

const EmailItem = styled(InfoItem)`
  grid-column: 3;
`;

const PhoneItem = styled(InfoItem)`
  grid-column: 4;
`;

// Updated to match PreviewModal
const ContentText = styled.div`
  font-size: 0.9rem;
  white-space: pre-line;
  font-family: ${FONTS.SECONDARY};
  color: ${COLORS.SECONDARY};
  font-weight: 600;
`;

// Updated to match PreviewModal
const OfferHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 2rem;
  padding-top: 1rem;
`;

// Updated to match PreviewModal
const OfferTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 800;
  color: ${DARK_BLUE};
  font-family: ${FONTS.PRIMARY};
  margin: 0;
  margin-right: 0.5rem;
  letter-spacing: 0.01em;
`;

// Updated to match PreviewModal - restored this component
const ConfidentialText = styled.span`
  font-size: 1.3rem;
  font-weight: 800;
  color: ${ACCENT_ORANGE};
  font-family: ${FONTS.SECONDARY};
  margin-left: 0.5rem;
`;

// Updated to match PreviewModal's RichContent
const RichContent = styled.div`
  font-size: 0.9rem;
  line-height: 1.5;
  font-family: ${FONTS.SECONDARY};
  color: ${COLORS.SECONDARY};
  font-weight: 600;
  
  p {
    margin-bottom: 0.75rem;
  }
  
  ul, ol {
    margin-left: 1.25rem;
    margin-bottom: 0.75rem;
  }
  
  strong {
    font-weight: 800;
  }
`;

// Estilos para archivos adjuntos
const FilePreview = styled.div`
  display: flex;
  align-items: center;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  margin-bottom: 0.5rem;
  background-color: #f9f9f9;
`;

const FileIcon = styled.div`
  margin-right: 1rem;
  color: #ff6b35;
  font-size: 1.5rem;
`;

const FileName = styled.p`
  flex: 1;
  font-size: 0.9rem;
  color: #333;
  margin: 0;
  font-family: ${FONTS.SECONDARY};
`;

const DownloadFileButton = styled.button`
  background: none;
  border: none;
  color: #4d7af7;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;

  &:hover {
    background-color: #f0f4ff;
    color: #3461e6;
  }

  &:disabled {
    color: #ccc;
    cursor: not-allowed;
  }
`;

const FilesSection = styled.div`
  margin-top: 1rem;
`;

const FilesSectionTitle = styled.h4`
  font-size: 0.9rem;
  font-weight: 600;
  color: ${COLORS.SECONDARY};
  margin-bottom: 0.75rem;
  font-family: ${FONTS.SECONDARY};
`;

const formatDate = (dateString) => {
  if (!dateString) return "";
  return formatDateToCustomFormat(dateString);
};

// Usar la función unificada de amountUtils
const formatAmount = formatAmountForDisplay;

const OfferDisplay = ({ offer, title, description }) => {
  if (!offer) return null;
  
  // Log the original data for debugging
  console.log("Original offer data:", offer);

  // Función para manejar descarga de archivos
  const handleDownloadFile = async (file) => {
    try {
      console.log("Descargando archivo:", file);
      await downloadOfferFile(offer.id, file.id, offer.token, file.name);
    } catch (error) {
      console.error("Error downloading file:", error);
      // Aquí podrías mostrar un mensaje de error al usuario
      alert(`Error al descargar el archivo: ${error.message}`);
    }
  };

  // Función para manejar descarga de archivos de reformulación
  const handleDownloadReformulationFile = async (file) => {
    try {
      console.log("Descargando archivo de reformulación:", file);
      console.log("ID de archivo que se enviará a la API:", file.reformulation_file_id || file.id);
      console.log("Tipo de archivo según la respuesta:", file.type);
      console.log("Nombre de archivo que se enviará:", file.file_name || file.name);
      await downloadReformulationFile(offer.id, file.reformulation_file_id || file.id, file.file_name || file.name);
    } catch (error) {
      console.error("Error downloading reformulation file:", error);
      // Aquí podrías mostrar un mensaje de error al usuario
      alert(`Error al descargar el archivo de reformulación: ${error.message}`);
    }
  };

  // Map API data to the format used in PreviewModal
  const mapApiDataToPreviewFormat = (offerData) => {
    // Extract data according to the new mapping requirements:
    // - Representante: should use "user" data  
    // - Parte representada: should use "offeror" data
    // - Contra: should use "counterparty" data
    // - Representada por: should use "lawyer" data
    
    // For representante data (use "user" from API)
    const userData = offerData.user || {};
    const representanteName = userData.firstName || userData.name || "";
    const representanteLastName = userData.lastNames || userData.lastName || "";
    
    // For parte representada data (use "offeror" from API)
    const offerorData = offerData.offeror || {};
    const parteRepresentadaName = offerorData.firstName || offerorData.name || "";
    const parteRepresentadaLastName = offerorData.lastNames || offerorData.lastName || "";
    
    // For contra data (use "counterparty" from API)
    const counterpartyData = offerData.counterparty || {};
    const contraName = counterpartyData.firstName || counterpartyData.name || "";
    const contraLastName = counterpartyData.lastNames || counterpartyData.lastName || "";
    
    // For representada por data (use "lawyer" from API)
    const lawyerData = offerData.lawyer || {};
    const representadaPorName = lawyerData.firstName || lawyerData.name || "";
    const representadaPorLastName = lawyerData.lastNames || lawyerData.lastName || "";
    
    // Create the formatted data structure
    return {
      representante: {
        nombre: representanteName,
        apellidos: representanteLastName,
        identificacion: userData.idValue || userData.identityNumber || userData.identificationNumber || "",
        email: userData.email || "",
        telefono: userData.mobile || userData.phone || "",
        numColegiado: userData.membershipNumber || userData.lawyerMembershipNumber || "",
        provinciaColegio: userData.province || userData.provinceName || ""
      },
      ofertante: {
        nombre: parteRepresentadaName,
        apellidos: parteRepresentadaLastName,
        identificacion: offerorData.idValue || offerorData.identityNumber || offerorData.identificationNumber || "",
        email: offerorData.email || "",
        movil: offerorData.mobile || offerorData.phone || ""
      },
      contraparte: {
        nombre: contraName,
        apellidos: contraLastName,
        identificacion: counterpartyData.idValue || counterpartyData.identityNumber || counterpartyData.identificationNumber || "",
        email: counterpartyData.email || "",
        movil: counterpartyData.mobile || counterpartyData.phone || ""
      },
      representacionContraparte: {
        activa: !!(representadaPorName || representadaPorLastName || lawyerData.idValue || lawyerData.identityNumber || lawyerData.identificationNumber || lawyerData.email || lawyerData.mobile || lawyerData.phone), // Only show if there's any lawyer data
        nombre: representadaPorName,
        apellidos: representadaPorLastName,
        identificacion: lawyerData.idValue || lawyerData.identityNumber || lawyerData.identificationNumber || "",
        email: lawyerData.email || "",
        movil: lawyerData.mobile || lawyerData.phone || ""
      },
      objeto: offerData.subject || "",
      confidencialidad: offerData.confidential || false,
      contenido: offerData.writtenContent || offerData.content || "",
      cuantia: offerData.amount || "",
      referencia: offerData.reference || ""
    };
  };

  // Transform API data to the required format
  const mappedData = mapApiDataToPreviewFormat(offer);
  console.log("Mapped data:", mappedData);
  
  // Format data for display
  const confidencialidad = mappedData.confidencialidad;
  const confidLabel = confidencialidad ? "Confidencial" : "No confidencial";

  return (
    <Container>
      {/* Title and description added to match edit/new offer screen */}
      {title && <Title>{title}</Title>}
      {description && <Description>{description}</Description>}
      
      <InfoHeader>
        <HeaderDate>
          <span>Enviada</span> &gt; {formatDate(offer.creationDate)}
        </HeaderDate>
      </InfoHeader>
      
      {/* Representative section */}
      <InfoBlock>
        <BlockTitle>Representante</BlockTitle>
        <TableGrid>
          <NameItem>
            {`${mappedData.representante.nombre} ${mappedData.representante.apellidos}`.trim()}
          </NameItem>
          <IdItem>{mappedData.representante.identificacion}</IdItem>
          <EmailItem>{mappedData.representante.email}</EmailItem>
          <PhoneItem>{mappedData.representante.telefono}</PhoneItem>
          <InfoItem>{mappedData.representante.numColegiado}</InfoItem>
          <InfoItem>{mappedData.representante.provinciaColegio}</InfoItem>
        </TableGrid>
      </InfoBlock>
      
      {/* Parte representada */}
      <InfoBlock>
        <BlockTitle>Parte representada</BlockTitle>
        <TableGrid>
          <NameItem>
            {`${mappedData.ofertante.nombre} ${mappedData.ofertante.apellidos}`.trim()}
          </NameItem>
          <IdItem>{mappedData.ofertante.identificacion}</IdItem>
          <EmailItem>{mappedData.ofertante.email}</EmailItem>
          <PhoneItem>{mappedData.ofertante.movil}</PhoneItem>
        </TableGrid>
      </InfoBlock>
      
      {/* Objeto de la controversia */}
      <InfoBlock>
        <BlockTitle>Objeto de la controversia:</BlockTitle>
        <ContentText>{mappedData.objeto}</ContentText>
      </InfoBlock>
      
      {/* Plazo de respuesta */}
      <InfoBlock>
        <BlockTitle>Plazo de respuesta:</BlockTitle>
        <ContentText>{formatDate(offer.deadlineToRespond || offer.offer_response_deadline)}</ContentText>
      </InfoBlock>
      
      {/* Contra */}
      <InfoBlock>
        <BlockTitle>Contra:</BlockTitle>
        <TableGrid>
          <NameItem>
            {`${mappedData.contraparte.nombre} ${mappedData.contraparte.apellidos}`.trim()}
          </NameItem>
          <IdItem>{mappedData.contraparte.identificacion}</IdItem>
          <EmailItem>{mappedData.contraparte.email}</EmailItem>
          <PhoneItem>{mappedData.contraparte.movil}</PhoneItem>
        </TableGrid>
      </InfoBlock>
      
      {/* Representada por */}
      {mappedData.representacionContraparte.activa && (
      <InfoBlock>
        <BlockTitle>Representada por:</BlockTitle>
        <TableGrid>
          <NameItem>
            {`${mappedData.representacionContraparte.nombre} ${mappedData.representacionContraparte.apellidos}`.trim()}
          </NameItem>
          <IdItem>{mappedData.representacionContraparte.identificacion}</IdItem>
          <EmailItem>{mappedData.representacionContraparte.email}</EmailItem>
          <PhoneItem>{mappedData.representacionContraparte.movil}</PhoneItem>
        </TableGrid>
      </InfoBlock>
      )}
      
      {/* Offer title and confidentiality - restored the confidentiality tag */}
      <OfferHeader>
        <OfferTitle>Oferta Vinculante</OfferTitle>
        <ConfidentialText>{confidLabel}</ConfidentialText>
      </OfferHeader>
      
      {/* Offer content */}
      <InfoBlock>
        <RichContent dangerouslySetInnerHTML={{ __html: mappedData.contenido }} />
      </InfoBlock>
      
      {/* Archivos adjuntos */}
      {offer.files && offer.files.length > 0 && (
        <InfoBlock>
          <BlockTitle>Archivos adjuntos:</BlockTitle>
          <FilesSection>
            {offer.files.map((file) => (
              <FilePreview key={file.id}>
                <FileIcon>📄</FileIcon>
                <FileName>{file.name}</FileName>
                <DownloadFileButton
                  onClick={() => handleDownloadFile(file)}
                  title="Descargar archivo"
                >
                  📁
                </DownloadFileButton>
              </FilePreview>
            ))}
          </FilesSection>
        </InfoBlock>
      )}

      {/* Reformulación - Solo mostrar si el estado es 'reformulated' */}
      {offer.status === 'reformulated' && (
        <InfoBlock>
          <BlockTitle>Reformulación de la oferta:</BlockTitle>
          
          {/* Mostrar nueva fecha límite si existe */}
          {offer.reformulation_deadline && (
            <div style={{ marginBottom: "1rem", fontSize: "0.9rem", color: COLORS.SECONDARY }}>
              <strong>Nueva fecha límite de respuesta:</strong> {formatDate(offer.reformulation_deadline)}
            </div>
          )}
          
          {/* Mostrar archivos de reformulación si existen */}
          {offer.reformulationFiles && offer.reformulationFiles.length > 0 && (
            <div style={{ marginBottom: "1rem" }}>
              <div style={{ fontSize: "0.9rem", fontWeight: "500", marginBottom: "0.5rem", color: COLORS.SECONDARY }}>
                Archivos de reformulación:
              </div>
              <FilesSection>
                {offer.reformulationFiles.map((file, index) => (
                  <FilePreview key={file.reformulation_file_id || index}>
                    <FileIcon>📄</FileIcon>
                    <FileName>{file.file_name || file.name}</FileName>
                    <DownloadFileButton
                      onClick={() => handleDownloadReformulationFile(file)}
                      title="Descargar archivo de reformulación"
                    >
                      📁
                    </DownloadFileButton>
                  </FilePreview>
                ))}
              </FilesSection>
            </div>
          )}
          
          {/* Mostrar contenido de texto de la reformulación si existe */}
          {(offer.reformulation_content || offer.reformulationText) && (
            <RichContent dangerouslySetInnerHTML={{ 
              __html: offer.reformulation_content || offer.reformulationText 
            }} />
          )}
          
          {/* Mensaje si no hay contenido de reformulación */}
          {!offer.reformulation_content && !offer.reformulationText && (!offer.reformulationFiles || offer.reformulationFiles.length === 0) && (
            <ContentText style={{ opacity: 0.6 }}>
              Sin contenido de reformulación disponible
            </ContentText>
          )}
        </InfoBlock>
      )}
    </Container>
  );
};

export default OfferDisplay; 